import { HTMLAttributes, ReactElement, useRef } from 'react';
import { useIsTextTruncated } from '@ardoq/hooks';
import { Container, IconContainer, Label } from './atoms';
import { PlainTextPopover, WithUncontrolledPopover } from '@ardoq/popovers';
import { Icon, IconName, IconSize } from '@ardoq/icons';

type ChipProps = {
  icon?: ReactElement<{ iconName: IconName; iconSize: IconSize }>;
  iconName?: IconName;
  isActive?: boolean;
  isDisabled?: boolean;
  onClick?: VoidFunction;
  popoverText?: string;
  dataTestId?: string;
} & Omit<HTMLAttributes<HTMLElement>, 'onClick'>;

export const Chip = ({
  className,
  icon,
  iconName,
  isActive,
  isDisabled,
  children,
  onClick,
  popoverText,
  style,
  dataTestId,
  ...rest // For backwards compatibility of DIV props
}: ChipProps) => {
  const labelRef = useRef<HTMLSpanElement>(null);
  const isLabelTruncated = useIsTextTruncated(labelRef);
  const isPopoverVisible = Boolean(popoverText) && isLabelTruncated;

  return (
    <WithUncontrolledPopover
      content={
        isPopoverVisible ? (
          <PlainTextPopover>{popoverText}</PlainTextPopover>
        ) : null
      }
    >
      <Container
        as={onClick ? 'button' : 'li'}
        {...(onClick && { type: 'button', disabled: isDisabled })}
        className={className}
        style={style}
        onClick={onClick}
        data-test-id={`filter-chip${dataTestId ? `-${dataTestId}` : ''}`}
        $isActive={isActive}
        $isDisabled={isDisabled}
        {...rest}
      >
        {icon && <IconContainer>{icon}</IconContainer>}
        {iconName && (
          <IconContainer>
            <Icon iconName={iconName} iconSize={IconSize.SMALL} />
          </IconContainer>
        )}
        <Label ref={labelRef}>{children}</Label>
      </Container>
    </WithUncontrolledPopover>
  );
};
